import React, { useState } from 'react';
import { Calculator, Loader2 } from 'lucide-react';
import { Label } from '../../components/ui/label';
import { orgApi } from '../phase567Api';
import { errorMessage } from '../errors';
import PickSelect from '../shared/PickSelect';
import MoneyInput from '../shared/MoneyInput';
import NumberInput from '../shared/NumberInput';

const usd = (n) => Number(n || 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// Ticket 202: preview what a plan pays on a deal (POST /v2/org/commissions/compute).
// Nothing is saved; this is a dry run.
export default function CommissionCalculator({ plans }) {
  const [planId, setPlanId] = useState('');
  const [loanAmount, setLoanAmount] = useState(null);
  const [gross, setGross] = useState(null);
  const [deals, setDeals] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const plan = plans.find((p) => p.plan_id === planId);
  const usesLoan = !plan || plan.division !== 'real_estate';
  const usesGross = !plan || plan.division !== 'mortgage';
  const negative = [loanAmount, gross, deals].some((n) => n != null && n < 0);

  // Any edit makes the shown result stale, so clear it.
  const edit = (set) => (v) => { set(v); setResult(null); setError(''); };

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      const data = await orgApi.compute({
        plan_id: planId,
        loan_amount: usesLoan ? loanAmount : null,
        gross_commission: usesGross ? gross : null,
        deal_count_this_month: deals || 0,
      });
      setResult(data.result);
    } catch (e) {
      setResult(null);
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 space-y-4" data-testid="commission-calculator">
      <div className="flex items-center gap-2">
        <Calculator size={16} className="text-slate-400" />
        <h2 className="font-serif text-lg font-bold" style={{ color: '#070B5F' }}>Commission calculator</h2>
        <span className="text-xs text-slate-500">Preview only. Nothing is saved.</span>
      </div>
      {plans.length === 0 ? (
        <p className="text-sm text-slate-500" data-testid="calc-no-plans">Add a commission plan first (Commission Plans tab).</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <PickSelect label="Plan" value={planId} onChange={edit(setPlanId)} placeholder="Choose a plan" testId="calc-plan"
              options={plans.map((p) => ({ value: p.plan_id, label: p.name }))} />
            {usesLoan && (
              <div><Label>Loan amount</Label><MoneyInput value={loanAmount} onChange={edit(setLoanAmount)} className="mt-1" data-testid="calc-loan" /></div>
            )}
            {usesGross && (
              <div><Label>Gross commission (real estate)</Label><MoneyInput value={gross} onChange={edit(setGross)} className="mt-1" data-testid="calc-gross" /></div>
            )}
            <div><Label>Deals closed this month</Label><NumberInput integer value={deals} onChange={edit(setDeals)} className="mt-1" data-testid="calc-deals" /></div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={run} disabled={!planId || busy || negative} className="btn-gold" data-testid="calc-run">
              {busy ? <Loader2 size={14} className="animate-spin" /> : 'Calculate'}
            </button>
            {negative && <span className="text-xs text-red-600">Amounts cannot be negative.</span>}
            {error && <span className="text-xs text-red-600" data-testid="calc-error">{error}</span>}
          </div>
          {result && (
            <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm" data-testid="calc-result">
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Gross</dt><dd data-testid="calc-out-gross">{usd(result.gross)}{result.cap_applied ? ' (capped)' : ''}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Bonus</dt><dd data-testid="calc-out-bonus">{usd(result.bonus)}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Deductions</dt><dd data-testid="calc-out-deductions">{usd(result.deductions)}</dd></div>
              <div><dt className="text-xs uppercase tracking-wider text-slate-500">Net</dt><dd className="font-semibold" style={{ color: '#070B5F' }} data-testid="calc-out-net">{usd(result.net)}</dd></div>
            </dl>
          )}
        </>
      )}
    </div>
  );
}
